import { useTranslation } from '@kostbar/i18n'
import type {
  ReportMeasure,
  ReportRow,
} from '@/features/reports/api/use-sales-report-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { formatCurrency, formatNumber } from '@/features/reports/lib/format'
import { WEEKDAY_SHORT } from '@/features/reports/lib/dimension-labels'

/** The hours a workplace lunch operation actually trades in. */
const HOURS = Array.from({ length: 15 }, (_, i) => i + 6)

/** Seven steps: one for "nothing at all", six for magnitude. */
const HEAT_CLASSES = [
  'bg-heat-0',
  'bg-heat-1',
  'bg-heat-2',
  'bg-heat-3',
  'bg-heat-4',
  'bg-heat-5',
  'bg-heat-6',
]

interface WeekdayHourHeatmapProps {
  rows: Array<ReportRow>
  measure: ReportMeasure
  /**
   * Rows of the same window restricted to the staffed channel. The hour the unattended tail starts
   * is read off these rather than configured: staffed hours differ per location and per season, and
   * a constant would state a falsehood about every store it was not written for. Empty or missing
   * means the note is left out — silence beats a made-up boundary.
   */
  staffedRows?: Array<ReportRow>
  loading?: boolean
}

/**
 * "When do we earn — and when does the till stand still" (CHG-2026-09-manager-reporting).
 *
 * A grid of cells rather than a chart library: a heatmap is exactly that, and Recharts has no
 * primitive for it. This way every cell is its own focusable element with its own tooltip, and it
 * prints.
 *
 * One hue, light to dark, with a scale legend — a value ramp is legitimate here because the axes
 * are ordered (weekday, hour) and the colour is the only place the third value can live. The
 * lightest step means *no revenue at all* and is deliberately a step of its own, so an empty cell
 * never reads as a small value.
 */
export function WeekdayHourHeatmap({
  rows,
  measure,
  staffedRows,
  loading,
}: WeekdayHourHeatmapProps) {
  const { t } = useTranslation('reports')

  // The owner's question is how much of the day the unattended tail is worth. The boundary is
  // whatever the data says it is at this location: the first hour after the last one that still
  // produced staffed revenue.
  const unattendedFrom = unattendedFromHour(staffedRows, measure)

  const byCell = new Map<string, number>()
  let max = 0
  let total = 0
  let afterEvening = 0
  for (const row of rows) {
    const value = measure === 'units' ? row.units : row.revenueCents
    byCell.set(row.key, value)
    if (value > max) max = value
    total += value
    if (unattendedFrom != null && hourOf(row.key) >= unattendedFrom)
      afterEvening += value
  }

  const format = (n: number) =>
    measure === 'units' ? formatNumber(n) : formatCurrency(n)

  return (
    <Card className="min-w-0">
      <CardHeader>
        <CardTitle className="text-sm font-semibold">
          {t('heatmap.title')}
        </CardTitle>
        <p className="text-xs text-muted-foreground">{t('heatmap.subtitle')}</p>
      </CardHeader>
      <CardContent className="min-w-0 space-y-4">
        {loading ? (
          <Skeleton className="h-52 w-full" />
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="size-3 rounded-sm border border-border bg-heat-0"
                  aria-hidden="true"
                />
                {t('heatmap.noRevenue')}
              </span>
              <span
                className="inline-flex items-center gap-1"
                aria-hidden="true"
              >
                <span>{t('heatmap.low')}</span>
                {HEAT_CLASSES.slice(1).map((cls) => (
                  <span key={cls} className={`h-3 w-4 rounded-sm ${cls}`} />
                ))}
                <span>{t('heatmap.high')}</span>
              </span>
            </div>

            <div className="overflow-x-auto">
              <div className="min-w-[560px]">
                <div
                  className="grid gap-[3px]"
                  style={{
                    gridTemplateColumns: `2.75rem repeat(${HOURS.length}, minmax(0, 1fr))`,
                  }}
                  role="table"
                  aria-label={t('heatmap.title')}
                >
                  <div />
                  {HOURS.map((hour) => (
                    <div
                      key={`h-${hour}`}
                      className="text-center text-[11px] text-muted-foreground"
                    >
                      {String(hour).padStart(2, '0')}
                    </div>
                  ))}

                  {WEEKDAY_SHORT.map((weekday, index) => (
                    <Row
                      key={weekday}
                      weekday={weekday}
                      isoDay={index + 1}
                      byCell={byCell}
                      max={max}
                      format={format}
                    />
                  ))}
                </div>
              </div>
            </div>

            {total > 0 && unattendedFrom != null && afterEvening > 0 ? (
              <p className="text-xs leading-5 text-muted-foreground">
                {t('heatmap.eveningHint', {
                  hour: unattendedFrom,
                  value: format(afterEvening),
                  share: eveningShare(afterEvening / total),
                })}
              </p>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  )
}

function Row({
  weekday,
  isoDay,
  byCell,
  max,
  format,
}: {
  weekday: string
  isoDay: number
  byCell: Map<string, number>
  max: number
  format: (n: number) => string
}) {
  return (
    <>
      <div className="text-xs text-muted-foreground">{weekday}</div>
      {HOURS.map((hour) => {
        const value =
          byCell.get(`${isoDay}-${String(hour).padStart(2, '0')}`) ?? 0
        return (
          <Tooltip key={`${isoDay}-${hour}`}>
            <TooltipTrigger asChild>
              {/* A button, not a div: the cell is the hit target, and the same detail has to be
                  reachable by keyboard as by pointer. */}
              <button
                type="button"
                className={`h-6 w-full rounded-sm ${HEAT_CLASSES[step(value, max)]} focus-visible:outline-2 focus-visible:outline-ring`}
                aria-label={`${weekday} ${String(hour).padStart(2, '0')}:00 — ${format(value)}`}
              />
            </TooltipTrigger>
            <TooltipContent>
              <span className="font-semibold tabular-nums">
                {format(value)}
              </span>
              <span className="ml-1.5 text-muted-foreground">
                {weekday} {String(hour).padStart(2, '0')}:00
              </span>
            </TooltipContent>
          </Tooltip>
        )
      })}
    </>
  )
}

/**
 * The first hour of the day at which nothing was sold over a staffed till any more.
 *
 * Derived, never configured. `null` — no staffed rows at all, or staffed revenue runs right up to
 * the last hour with any revenue — means there is no unattended tail worth naming, and the note is
 * dropped rather than guessed at.
 */
function unattendedFromHour(
  staffedRows: Array<ReportRow> | undefined,
  measure: ReportMeasure,
): number | null {
  if (!staffedRows || staffedRows.length === 0) return null
  let lastStaffedHour = -1
  for (const row of staffedRows) {
    const value = measure === 'units' ? row.units : row.revenueCents
    if (value <= 0) continue
    const hour = hourOf(row.key)
    if (Number.isFinite(hour) && hour > lastStaffedHour) lastStaffedHour = hour
  }
  if (lastStaffedHour < 0 || lastStaffedHour >= 23) return null
  return lastStaffedHour + 1
}

const eveningShareFormatter = new Intl.NumberFormat('de-AT', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

function eveningShare(share: number): string {
  return eveningShareFormatter.format(share)
}

function hourOf(key: string): number {
  return Number(key.split('-')[1])
}

/** Zero is its own step; everything else spreads over the remaining six. */
function step(value: number, max: number): number {
  if (value <= 0 || max <= 0) return 0
  return Math.min(6, Math.max(1, Math.ceil((value / max) * 6)))
}
